import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import MobileNav from './MobileNav';
import { useMobile } from '@/hooks/useMobile';
import { useTheme } from '@/context/ThemeContext';

const drawerItems = [
  { label: 'Dashboard', path: '/', icon: 'ri-dashboard-3-line' },
  { label: 'Randevular', path: '/randevular', icon: 'ri-calendar-check-line' },
  { label: 'Online Randevu', path: '/online-randevu', icon: 'ri-global-line' },
  { label: 'Müşteriler', path: '/musteriler', icon: 'ri-user-smile-line' },
  { label: 'Güvenlik', path: '/güvenlik', icon: 'ri-shield-keyhole-line' },
  { label: 'Hizmetler', path: '/hizmetler', icon: 'ri-drop-line' },
  { label: 'Paketler', path: '/paketler', icon: 'ri-vip-crown-line' },
  { label: 'Kasa', path: '/kasa', icon: 'ri-safe-2-line' },
  { label: 'Giderler', path: '/giderler', icon: 'ri-money-cny-circle-line' },
  { label: 'Personel', path: '/personel', icon: 'ri-team-line' },
  { label: 'Stok', path: '/stok', icon: 'ri-stack-line' },
  { label: 'Kampanyalar', path: '/kampanyalar', icon: 'ri-megaphone-line' },
  { label: 'WhatsApp/SMS', path: '/whatsapp-sms', icon: 'ri-whatsapp-line' },
  { label: 'Raporlar', path: '/raporlar', icon: 'ri-bar-chart-box-line' },
  { label: 'Abonelik', path: '/abonelik', icon: 'ri-vip-diamond-line' },
  { label: 'Ayarlar', path: '/ayarlar', icon: 'ri-settings-4-line' },
];

export default function AppShell({ children }: { children: React.ReactNode }) {
  const isMobile = useMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-theme-primary transition-colors duration-400">
      {/* Sidebar - Desktop */}
      <Sidebar />

      {/* Top Bar - Desktop */}
      {!isMobile && <TopBar />}

      {/* Mobile Header */}
      {isMobile && (
        <header
          className="fixed top-0 left-0 right-0 z-30 h-14 flex items-center justify-between px-4"
          style={{
            background: 'var(--topbar-bg)',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div className="flex items-center gap-2">
            <img
              src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/1d51f5ffde24df2b1cca180a545c35bd.jpeg"
              alt="Kuğu Spa"
              className="w-8 h-8 rounded-full object-cover"
            />
            <span className="font-display font-semibold text-base" style={{ color: 'var(--text-primary)' }}>
              Kuğu Spa
            </span>
          </div>
          <button
            onClick={toggleTheme}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: 'var(--bg-tertiary)' }}
          >
            <i className={theme === 'light' ? 'ri-moon-line text-sm' : 'ri-sun-line text-sm'} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </header>
      )}

      {/* Main Content */}
      <main
        className="transition-all duration-300"
        style={{
          marginLeft: isMobile ? 0 : '16rem',
          paddingTop: isMobile ? '3.5rem' : '4rem',
          paddingBottom: isMobile ? '4rem' : '1.5rem',
          paddingLeft: isMobile ? '1rem' : '1.5rem',
          paddingRight: isMobile ? '1rem' : '1.5rem',
          minHeight: '100vh',
        }}
      >
        <div className="max-w-[1440px] mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileNav onOpenDrawer={() => setDrawerOpen(true)} />}

      {/* Mobile Drawer */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-50 lg:hidden"
            style={{ background: 'var(--modal-overlay)' }}
            onClick={() => setDrawerOpen(false)}
          />
          <div
            className="fixed right-0 top-0 bottom-0 z-50 w-[280px] lg:hidden flex flex-col animate-slide-in"
            style={{ background: 'var(--sidebar-bg)' }}
          >
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center gap-2">
                <img
                  src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/1d51f5ffde24df2b1cca180a545c35bd.jpeg"
                  alt="Kuğu Spa"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Menü</span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
              {drawerItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setDrawerOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-spa-sm text-sm font-medium transition-all cursor-pointer ${
                      active ? 'bg-theme-sidebar-active' : 'hover:bg-theme-hover'
                    }`}
                    style={active ? { borderLeft: '3px solid var(--gold)' } : { borderLeft: '3px solid transparent' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`} style={{ color: active ? 'var(--gold)' : 'var(--text-secondary)' }} />
                    </div>
                    <span className="truncate" style={{ color: active ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </>
      )}
    </div>
  );
}